import { useState, useEffect, Fragment } from "react";
import { Typography, Box, Link, LinearProgress, Skeleton } from "@mui/material";
import CachedIcon from "@mui/icons-material/Cached";
import { convertDataFromBytes } from "../../utils/commonFunctions/ConvertDataFromBytes";
import { format } from "date-fns";
import "./DataUsageInfo.scss";
import { useAppSelector } from "../../app/hooks";
import { SecondaryLinesDataUsage } from "./SecondaryLinesDataUsage";
import { CustomerStatus } from "../../enums";
import { NotificationCust } from "../widgets";
import EnvConfig from "../../config/env.config.json";
import { RoutingURLs } from "../../config/RoutingURLs";

import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import { DataUsageFailure } from "../widgets";

interface IDataUsage {
  dataUsage: any;
  startDate: Date | number;
  endDate: Date | number;
  handleBillCycleChange?: any;
  selectedDateKeyIndex: number;
  totalNumberOfKeys: number;
  content: any;
  fetchDataUsageDetails: any;
  showSecondaryLinesUsage: boolean;
  showDataUsageForUnlimitedPack: boolean;
  callBackGetCustomerGroup: any;
  handleBuyData?: any;
  showDataExceedingNotification?: boolean;
  loading?: boolean;
  setLoadingData?: any;
  isCardExpired: any;
}
export const DataUsageInfo = (props: IDataUsage) => {
  const {
    dataUsage,
    startDate,
    endDate,
    handleBillCycleChange,
    selectedDateKeyIndex,
    totalNumberOfKeys,
    content,
    fetchDataUsageDetails,
    showSecondaryLinesUsage,
    showDataUsageForUnlimitedPack,
    callBackGetCustomerGroup,
    handleBuyData,
    showDataExceedingNotification,
    loading,
    setLoadingData,
    isCardExpired,
  } = props;
  const { group } = useAppSelector((state: any) => state.account);
  const { getCustomer } = useAppSelector((state: any) => state.checkout);
  const { customerBillCycleDataUsage, errorCustomerBillCycleDataUsage } =
    useAppSelector((state: any) => state.customer);
  const [groupData, setGroupData] = useState<Array<any>>([]);
  const [primaryCustomer, setPrimaryCustomer] = useState();
  const currentPath = window.location.href;

  const getDataUsageValue = (usageData: any, totalPlanData: any) => {
    let usageDataValue = parseInt(usageData, 10);
    let totalPlanDataValue = parseInt(totalPlanData, 10);

    if (usageDataValue >= totalPlanDataValue) {
      return convertDataFromBytes(totalPlanDataValue);
    } else {
      return convertDataFromBytes(usageData);
    }
  };
  useEffect(() => {
    if (group?.data && group?.data.length > 0) {
      let sortedGroupUsersByDate = group?.data
        ?.slice()
        .sort((a: any, b: any) => {
          return (
            new Date(a.createdDate).getTime() -
            new Date(b.createdDate).getTime()
          );
        });
      setGroupData(sortedGroupUsersByDate);
      const primaryUser = sortedGroupUsersByDate.find(
        (user: any) => user.isPrimary === true
      );
      setPrimaryCustomer(primaryUser);
    }
  }, [group]);
  const isEligebleUser = (status: number) => {
    return [
      CustomerStatus.ACTIVE,
      CustomerStatus.SUSPENDED,
      CustomerStatus.DISCONNECTED,
      CustomerStatus.HOTLINED,
      CustomerStatus.REJECTED,
      CustomerStatus.DISABLED,
    ].includes(status);
  };
  let usageData = 0;
  let totalPlanData = 0;
  const isSharablePlan = dataUsage?.group;
  const lineDatausage = () => {
    const primaryCustomerDataUsage = dataUsage?.users.find(
      (user: any) => user.userId === getCustomer.id
    );
    usageData = primaryCustomerDataUsage?.bytes || 0;
    totalPlanData = primaryCustomerDataUsage?.maxBytes || 0;
  };
  if (EnvConfig?.IS_SHOW_INDIVIDUAL_DATA_USAGE) {
    lineDatausage();
  } else {
    if (isSharablePlan) {
      usageData = dataUsage?.group?.bytes;
      totalPlanData = dataUsage?.group?.maxBytes;
    } else {
      lineDatausage();
    }
  }
  const getDataUsagePercentage = () => {
    if (usageData <= totalPlanData) {
      return (usageData / totalPlanData) * 100 || 0;
    } else {
      return 100 || 0;
    }
  };
  useEffect(() => {
    /**
     * For unlimited pack, if atleast one user consumes more then 90% data usage then considering that user for showing data usage alert
     */
    let isCrossingCustomerUnlimitePlanDataUsage = false;
    const nearCrossingCustomerDataUsage = dataUsage?.users.find(
      (user: any) => ((user.bytes / user.maxBytes) * 100 || 0) >= 90
    );
    if (!isSharablePlan && nearCrossingCustomerDataUsage) {
      isCrossingCustomerUnlimitePlanDataUsage = true;
    }
    if (
      ((getDataUsagePercentage() >= 90 && getDataUsagePercentage() < 100) ||
        isCrossingCustomerUnlimitePlanDataUsage) &&
      showDataExceedingNotification &&
      content?.d_u_n_e
    ) {
      NotificationCust({
        message: content?.d_u_n_e,
        type: "info",
        id: "dataUsageNotification",
        showIcon: true,
      });
    }
  }, [usageData]);

  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
      className="data_usage_wrapper"
    >
      <Typography className="data_usage_label">{content?.data_usg}</Typography>
      {dataUsage ? (
        <>
          <Box className="bill_cycle_label">
            {currentPath.includes(RoutingURLs.account) &&
            totalNumberOfKeys > 0 &&
            selectedDateKeyIndex > 0 ? (
              <Box
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
                onClick={() => handleBillCycleChange("decrement")}
              >
                <ArrowBackIosNewRoundedIcon sx={{ fontSize: "14px" }} />
              </Box>
            ) : null}
            &nbsp;
            {startDate && endDate
              ? `${format(startDate, "MMM dd, yyyy")} - ${format(
                  endDate,
                  "MMM dd, yyyy"
                )}`
              : null}
            &nbsp;
            {totalNumberOfKeys > 0 &&
            selectedDateKeyIndex !== totalNumberOfKeys - 1 ? (
              <Box
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
                onClick={() => handleBillCycleChange("increment")}
              >
                <ArrowForwardIosRoundedIcon sx={{ fontSize: "14px" }} />
              </Box>
            ) : null}
          </Box>
          {EnvConfig.SUSPENDED_DISCONNECTED_LINE_TEXT &&
            (getCustomer?.status === CustomerStatus.SUSPENDED ||
              getCustomer?.status === CustomerStatus.HOTLINED) &&
            currentPath.includes(RoutingURLs.postActivationHome) && (
              <Box
                className="description_text"
                width={{ xs: "90%", sm: "70%", md: "60%" }}
                textAlign="center"
                pt="20px"
              >
                {content?.suspended_txt}
              </Box>
            )}
          {(isSharablePlan || showDataUsageForUnlimitedPack) && dataUsage && (
            <>
              <Box className="data_usage_txt">
                <Typography
                  component="p"
                  className="data_usage_txt"
                  color={"var(--primary_brand_color)"}
                >
                  {getDataUsageValue(usageData, totalPlanData)}
                </Typography>
                <Typography component="p" className="data_usage_txt">
                  &nbsp;{content?.of}&nbsp;
                </Typography>
                <Typography component="p" className="data_usage_txt">
                  {convertDataFromBytes(totalPlanData)}
                </Typography>
                {selectedDateKeyIndex === totalNumberOfKeys - 1 && (
                  <Link
                    display="flex"
                    alignItems="center"
                    justifyItems="center"
                  >
                    {loading ? (
                      <>
                        <CachedIcon
                          className="load_icon_gif"
                          fontSize="large"
                        />
                      </>
                    ) : (
                      <CachedIcon
                        className="load_icon"
                        onClick={() => {
                          setLoadingData(true);
                          fetchDataUsageDetails();
                        }}
                        style={{ cursor: "pointer" }}
                        fontSize="large"
                      />
                    )}
                  </Link>
                )}
              </Box>
              <Box
                className="progress_bar"
                width={{ xs: "100%", sm: "500px" }}
                sx={{
                  maxWidth: {
                    xs: "90%",
                    md: "100%",
                  },
                }}
              >
                <LinearProgress
                  variant="determinate"
                  value={getDataUsagePercentage()}
                  sx={{
                    m: "10px auto 15px",
                    height: "11px",
                    width: { xs: "80%", sm: "100%" },
                    borderRadius: "5.5px",
                    backgroundColor: "hsla(0,0%,6.8%,.10196078431372549)",
                  }}
                />
              </Box>
            </>
          )}
        </>
      ) : errorCustomerBillCycleDataUsage ||
        (customerBillCycleDataUsage &&
          !Object.keys(customerBillCycleDataUsage).length) ? (
        <DataUsageFailure
          forLines={currentPath === RoutingURLs.account ? true : false}
        />
      ) : (
        <>
          <Skeleton variant="rounded" width={210} />
          <Skeleton animation="wave" width="100%" />
          <Skeleton animation="wave" width="100%" />
        </>
      )}
      {showSecondaryLinesUsage &&
        groupData &&
        groupData.map((groupUser: any) => {
          const customer = dataUsage
            ? dataUsage?.users.find(
                (dataUsageUser: any) => dataUsageUser.userId === groupUser.id
              )
            : groupUser;
          if (customer && isEligebleUser(groupUser.status)) {
            return (
              <Fragment key={groupUser.id}>
                <SecondaryLinesDataUsage
                  user={customer}
                  groupUser={groupUser}
                  isSharablePlan={isSharablePlan}
                  isCardExpired={isCardExpired}
                  isCurrentDataUsage={
                    selectedDateKeyIndex === totalNumberOfKeys - 1
                  }
                  primaryCustomer={primaryCustomer}
                  callBackGetCustomerGroup={callBackGetCustomerGroup}
                  content={content}
                  handleBuyData={handleBuyData}
                />
              </Fragment>
            );
          }
        })}
    </Box>
  );
};
